import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Download,
  Loader2,
  FileText,
  Image as ImageIcon,
  Film,
  Music,
  Archive,
  File as FileIcon,
  Folder,
  Eye,
  Mail,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { ASSETS } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtBytes(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function iconFor(ctype) {
  if (!ctype) return FileIcon;
  if (ctype.startsWith("image/")) return ImageIcon;
  if (ctype.startsWith("video/")) return Film;
  if (ctype.startsWith("audio/")) return Music;
  if (ctype.includes("zip")) return Archive;
  if (
    ctype === "application/pdf" ||
    ctype.includes("word") ||
    ctype.includes("document") ||
    ctype.startsWith("text/") ||
    ctype.includes("presentation") ||
    ctype.includes("sheet")
  )
    return FileText;
  return FileIcon;
}

const isVisual = (a) => (a.content_type || "").startsWith("image/");

/**
 * Public viewer for a shared folder or a shared subcategory ("the monthly packet").
 * Route: /library/share/folder/:token  or  /library/share/subcategory/:token
 */
export default function LibraryBundleShare({ kind }) {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: "" });

  useEffect(() => {
    document.title = "Great Rate Check - MLO Resource Page";
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/library/share/${kind}/${token}/meta`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          if (!cancelled) setState({ loading: false, data: null, error: data.detail || "This link isn't valid." });
          return;
        }
        const data = await res.json();
        if (!cancelled) setState({ loading: false, data, error: "" });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: "Could not load. Please try again." });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, token]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#1F8F8B]" />
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center rounded-3xl bg-white border border-[#EDE9DC] p-10 shadow-sm">
          <h1 className="text-xl font-semibold text-[#0F2A47]">Link unavailable</h1>
          <p className="text-sm text-[#59564C] mt-2">{state.error || "This share link has expired or was revoked."}</p>
          <Link to="/" className="inline-block mt-6 text-sm text-[#1F8F8B] hover:underline">Return home</Link>
        </div>
      </div>
    );
  }

  const { data } = state;
  const isFolder = data.kind === "folder";
  const month = new Date().toLocaleString("en-US", { month: "long" });
  const title = isFolder ? data.folder?.name : `Your ${month} Marketing Packet`;
  const fromLine = data.issuer_name ? `From ${data.issuer_name} · Great Rate Check` : "Great Rate Check · Partner Assets";
  const subLine = isFolder
    ? `${data.subcategory?.name || "Partner assets"} · ${data.count} item${data.count === 1 ? "" : "s"}`
    : `${data.subcategory?.name || ""} · ${data.count} item${data.count === 1 ? "" : "s"} · shared with you`;
  const assetBaseUrl = `${API}/library/share/${kind}/${token}/asset`;
  const packet = data.packet || {};
  const contact = data.contact || {};
  const hasOverview = !isFolder && (packet.overview || packet.theme || packet.first_action);

  // For subcategory shares, mirror the library structure: group assets by folder.
  const folderGroups = [];
  if (!isFolder && (data.folders || []).length > 0) {
    for (const f of data.folders) {
      const items = data.assets.filter((a) => a.folder_id === f.id);
      if (items.length) folderGroups.push({ id: f.id, name: f.name, description: f.description || "", items });
    }
    const ungrouped = data.assets.filter((a) => !a.folder_id || !data.folders.some((f) => f.id === a.folder_id));
    if (ungrouped.length) folderGroups.push({ id: "__ungrouped", name: "More files", description: "", items: ungrouped });
  }
  const useGroups = folderGroups.some((g) => g.id !== "__ungrouped");

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="library-bundle-share">
      <header
        className="text-white"
        style={{ background: "linear-gradient(135deg, #08192F 0%, #0F2A47 50%, #17385e 100%)" }}
      >
        <div className="max-w-5xl mx-auto px-6 py-8 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="inline-flex bg-white rounded-2xl px-3 py-1.5 shadow">
              <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-9 sm:h-10 w-auto" />
            </Link>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#A6D24A]" data-testid="bundle-from-line">{fromLine}</div>
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{title}</h1>
              {!isFolder && packet.directive && (
                <p className="mt-1.5 text-[15px] font-semibold text-[#A6D24A]" data-testid="packet-directive">{packet.directive}</p>
              )}
              <p className="text-sm text-white/70 mt-1 capitalize">{subLine}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Overview + theme + first action */}
        {hasOverview && (
          <div className="mb-8 rounded-2xl bg-white border border-[#EDE9DC] border-l-4 border-l-[#A6D24A] p-5 sm:p-6 shadow-sm" data-testid="packet-overview">
            {packet.theme && (
              <div className="inline-flex items-center gap-1.5 rounded-full bg-[#0F2A47] text-[#A6D24A] text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 mb-3" data-testid="packet-theme">
                <Sparkles className="w-3 h-3" /> This month: {packet.theme}
              </div>
            )}
            {packet.overview && (
              <p className="text-[15px] text-[#0F2A47] leading-relaxed">{packet.overview}</p>
            )}
            {packet.first_action && (
              <div className="mt-3 flex items-start gap-2 rounded-xl bg-[#A6D24A22] border border-[#A6D24A] px-4 py-3" data-testid="packet-first-action">
                <ArrowRight className="w-4 h-4 text-[#0F2A47] shrink-0 mt-0.5" />
                <p className="text-sm font-semibold text-[#0F2A47]">Start here: {packet.first_action}</p>
              </div>
            )}
          </div>
        )}

        {data.assets.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-[#D9D3BF] bg-white/60 py-16 text-center">
            <Folder className="w-8 h-8 text-[#7A7668] mx-auto mb-2" />
            <p className="text-[#0F2A47] font-medium">Nothing shared here.</p>
            <p className="text-sm text-[#7A7668] mt-1">Ask the sender to add files and re-share.</p>
          </div>
        ) : useGroups ? (
          <div className="space-y-9" data-testid="bundle-folder-groups">
            {folderGroups.map((g) => (
              <FolderSection key={g.id} group={g} assetBaseUrl={assetBaseUrl} />
            ))}
          </div>
        ) : (
          <GroupContents
            items={data.assets}
            assetBaseUrl={assetBaseUrl}
            description={isFolder ? data.folder?.description : ""}
          />
        )}

        {/* Questions CTA */}
        {contact.email && (
          <div className="mt-12 rounded-2xl bg-[#0F2A47] text-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5" data-testid="bundle-contact-cta">
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-widest text-[#A6D24A] font-bold">Questions?</div>
              <p className="mt-1.5 text-lg font-semibold">Not sure how to use something here?</p>
              <p className="text-sm text-white/70 mt-1">{contact.name} can help — just reply by email.</p>
            </div>
            <a
              href={`mailto:${contact.email}?subject=${encodeURIComponent("Question about my marketing packet")}`}
              data-testid="bundle-contact-email"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#A6D24A] hover:bg-[#94bd41] text-[#08192F] font-semibold px-6 py-3 text-sm transition-colors shrink-0"
            >
              <Mail className="w-4 h-4" /> Email {contact.name?.split(" ")[0] || "us"}
            </a>
          </div>
        )}
      </main>

      <footer className="max-w-5xl mx-auto px-6 pb-10 text-xs text-[#7A7668]">
        Great Rate Check · NEXA Mortgage LLC · Materials on this page are for authorized recipients only.
      </footer>
    </div>
  );
}

function FolderSection({ group, assetBaseUrl }) {
  return (
    <section data-testid={`bundle-group-${group.id}`}>
      <div className="flex items-center gap-2.5">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#0F2A47] text-[#A6D24A]">
          <Folder className="w-4 h-4" />
        </span>
        <h2 className="text-lg font-semibold text-[#0F2A47]">{group.name}</h2>
        <span className="text-xs text-[#7A7668] bg-white border border-[#EDE9DC] rounded-full px-2 py-0.5 tabular-nums">
          {group.items.length}
        </span>
      </div>
      {group.description && (
        <p className="mt-1.5 text-sm text-[#59564C] max-w-2xl" data-testid={`bundle-group-desc-${group.id}`}>{group.description}</p>
      )}
      <div className="mt-4">
        <GroupContents items={group.items} assetBaseUrl={assetBaseUrl} />
      </div>
    </section>
  );
}

function GroupContents({ items, assetBaseUrl, description }) {
  const visuals = items.filter(isVisual);
  const docs = items.filter((a) => !isVisual(a));
  return (
    <div>
      {description && <p className="mb-4 text-sm text-[#59564C] max-w-2xl">{description}</p>}
      {docs.length > 0 && (
        <div className="rounded-2xl bg-white border border-[#EDE9DC] divide-y divide-[#F0EDE2] overflow-hidden">
          {docs.map((a) => (
            <DocRow key={a.id} asset={a} assetBaseUrl={assetBaseUrl} />
          ))}
        </div>
      )}
      {visuals.length > 0 && (
        <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 ${docs.length > 0 ? "mt-4" : ""}`}>
          {visuals.map((a) => (
            <ThumbCard key={a.id} asset={a} assetBaseUrl={assetBaseUrl} />
          ))}
        </div>
      )}
    </div>
  );
}

function DocRow({ asset, assetBaseUrl }) {
  const Icon = iconFor(asset.content_type);
  const previewUrl = `${assetBaseUrl}/${asset.id}/preview`;
  const downloadUrl = `${assetBaseUrl}/${asset.id}/download`;
  const ext = (asset.filename?.split(".").pop() || "file").toUpperCase().slice(0, 5);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex items-center gap-3 px-4 py-3 hover:bg-[#FBF9F2] transition-colors"
      data-testid={`bundle-asset-${asset.id}`}
    >
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#F4F2EB] text-[#1F8F8B] shrink-0">
        <Icon className="w-[18px] h-[18px]" strokeWidth={1.6} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#0F2A47] truncate" title={asset.filename}>{asset.filename}</p>
        <p className="text-[11px] text-[#7A7668] truncate">
          {asset.description ? `${asset.description} · ` : ""}{ext} · {fmtBytes(asset.size)}
        </p>
      </div>
      <a
        href={previewUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`bundle-preview-${asset.id}`}
        className="inline-flex items-center gap-1.5 rounded-lg border border-[#EDE9DC] hover:border-[#1F8F8B] hover:text-[#1F8F8B] text-[#0F2A47] px-2.5 py-1.5 text-xs shrink-0"
      >
        <Eye className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Open</span>
      </a>
      <a
        href={downloadUrl}
        data-testid={`bundle-download-${asset.id}`}
        className="inline-flex items-center gap-1.5 rounded-lg bg-[#0F2A47] hover:bg-[#173a5f] text-white px-2.5 py-1.5 text-xs shrink-0"
      >
        <Download className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Download</span>
      </a>
    </motion.div>
  );
}

function ThumbCard({ asset, assetBaseUrl }) {
  const previewUrl = `${assetBaseUrl}/${asset.id}/preview`;
  const downloadUrl = `${assetBaseUrl}/${asset.id}/download`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="group rounded-xl overflow-hidden bg-white border border-[#EDE9DC] hover:shadow-lg transition"
      data-testid={`bundle-asset-${asset.id}`}
    >
      <a
        href={previewUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`bundle-preview-${asset.id}`}
        className="block w-full aspect-square bg-[#F4F2EB] relative overflow-hidden"
      >
        <img src={previewUrl} alt={asset.filename} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
      </a>
      <div className="px-2.5 py-2 flex items-center gap-1.5">
        <p className="flex-1 text-[11px] font-medium text-[#0F2A47] truncate" title={asset.filename}>{asset.filename}</p>
        <a
          href={downloadUrl}
          data-testid={`bundle-download-${asset.id}`}
          title="Download"
          className="text-[#7A7668] hover:text-[#1F8F8B] p-1 shrink-0"
        >
          <Download className="w-3.5 h-3.5" />
        </a>
      </div>
    </motion.div>
  );
}
